<script setup>
import { ref } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const props = defineProps({
  asset: Object,
});

const emit = defineEmits(['update']);

function update(id, tag) {
  emit('update', id, tag);
}

const isPopoverOpen = ref(false);
const selectedUsage = ref(props.asset?.tag || '');
const customUsage = ref('');

function selectUsage(usage) {
  selectedUsage.value = usage;
  updateAssetTags();
}

function addCustomUsage() {
  if (customUsage.value.trim()) {
    selectedUsage.value = customUsage.value.trim();
    customUsage.value = '';
    updateAssetTags();
  }
}

function updateAssetTags() {
  update(props.asset.id, selectedUsage.value);
}

function isUsageSelected(usage) {
  return selectedUsage.value === usage;
}

const materialUsages = [
  { id: 'background', label: 'material.background' },
  { id: 'product', label: 'material.product' },
  { id: 'model', label: 'material.model' },
  { id: 'logo', label: 'material.logo' },
  { id: 'qrcode', label: 'material.qrcode' },
  { id: 'icon', label: 'material.icon' },
  { id: 'decoration', label: 'material.decoration' },
  { id: 'texture', label: 'material.texture' },
  { id: 'chart', label: 'material.chart' },
];
</script>
<template>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger as-child>
      <slot />
    </PopoverTrigger>

    <PopoverContent class="w-80 p-0 shadow-xl rounded-2xl border-0 bg-white/95 backdrop-blur-xl">
      <div class="p-5">
        <!-- Header -->
        <div class="flex gap-1 mb-4">
          <h3 class="text-slate-800 text-sm font-semibold tracking-tight">
            {{ $t('material.selectUsage') }}
          </h3>
          <p class="text-slate-500 text-xs leading-relaxed">
            {{ $t('material.singleSelect') }}
          </p>
        </div>

        <!-- Usage selection grid -->
        <div class="grid grid-cols-3 gap-2 mb-4">
          <button
            v-for="usage in materialUsages"
            :key="usage.id"
            :class="[
              'group/tag relative p-3 rounded-xl border transition-all duration-200 text-left hover:scale-[1.02]',
              isUsageSelected($t(usage.label))
                ? 'bg-gradient-to-br from-[#20f6da]/10 to-[#86EEBA]/10 border-[#20f6da]/30 shadow-sm'
                : 'bg-white/60 border-slate-200/80 hover:border-[#20f6da]/20 hover:bg-slate-50/80',
            ]"
            @click="selectUsage($t(usage.label))"
          >
            <!-- Selection indicator -->
            <div
              :class="[
                'absolute top-2 right-2 w-2 h-2 rounded-full transition-all duration-200',
                isUsageSelected($t(usage.label))
                  ? 'bg-[#20f6da] shadow-lg shadow-[#20f6da]/30'
                  : 'bg-slate-200 group-hover/tag:bg-slate-300',
              ]"
            ></div>

            <!-- Label -->
            <span
              :class="[
                'text-xs font-medium transition-colors duration-200 block',
                isUsageSelected($t(usage.label))
                  ? 'text-slate-800'
                  : 'text-slate-600 group-hover/tag:text-slate-700',
              ]"
            >
              {{ $t(usage.label) }}
            </span>
          </button>
        </div>

        <!-- Custom usage input -->
        <div class="space-y-3">
          <div class="flex items-center gap-2 text-xs text-slate-500">
            <div class="h-px bg-slate-200 flex-1"></div>
            <span class="px-2">{{ $t('material.or') }}</span>
            <div class="h-px bg-slate-200 flex-1"></div>
          </div>

          <div class="flex items-center gap-2">
            <input
              v-model="customUsage"
              type="text"
              :placeholder="$t('material.customUsagePlaceholder')"
              class="flex-1 text-sm px-3 py-2.5 border border-slate-200/80 rounded-xl bg-white/60 focus:outline-none focus:border-[#20f6da]/40 focus:bg-white transition-all duration-200 placeholder:text-slate-400"
              @keyup.enter="addCustomUsage"
            />
            <button
              @click="addCustomUsage"
              :disabled="!customUsage.trim()"
              class="px-4 py-2.5 bg-gradient-to-r from-[#20f6da] to-[#86EEBA] text-white text-sm font-medium rounded-xl hover:shadow-lg hover:shadow-[#20f6da]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
            >
              {{ $t('material.add') }}
            </button>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
